import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { gamingAPI } from '../services/api';
import { HardDrive } from 'lucide-react';
import toast from 'react-hot-toast';

const SteamPersistToggle = ({ userId }) => {
  const [enabled, setEnabled] = useState(false);
  const queryClient = useQueryClient();

  const { isLoading } = useQuery(
    ['preferences', userId],
    () => gamingAPI.getPreferences(),
    {
      enabled: !!userId,
      onSuccess: (data) => {
        setEnabled(!!data.sync_steam_games);
      },
    }
  );

  const mutation = useMutation(
    (sync_steam_games) => gamingAPI.updatePreferences({ sync_steam_games }),
    {
      onSuccess: (data) => {
        setEnabled(!!data.sync_steam_games);
        toast.success(
          data.sync_steam_games
            ? 'Games will copy to the NAS when this GPU is destroyed'
            : 'Only Steam login is kept — games stay on the GPU'
        );
        queryClient.invalidateQueries(['preferences', userId]);
      },
      onError: () => toast.error('Could not update Steam copy preference'),
    }
  );

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-600 bg-gray-800/60 p-4 animate-pulse h-20" />
    );
  }

  return (
    <div
      className={`rounded-lg p-4 border ${
        enabled
          ? 'border-sky-500/40 bg-sky-950/30'
          : 'border-gray-600 bg-gray-800/60'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-full ${enabled ? 'bg-sky-500/20' : 'bg-gray-700'}`}>
            <HardDrive className={`h-5 w-5 ${enabled ? 'text-sky-300' : 'text-gray-400'}`} />
          </div>
          <div>
            <h3 className="text-white font-semibold">
              Keep Steam on the NAS
            </h3>
            <p className="text-sm text-white/70">
              Steam Cloud keeps saves and some settings — not the game install.
              Dota 2 (~40 GB) lives on this GPU disk and must download again on a
              new machine unless you turn this on. Login is always saved
              (config + session files — not your password), so Steam Guard
              usually does not ask again.
              {enabled
                ? ' Games are also copied over SSH when you destroy this machine, then unpacked onto the next GPU disk. This is a copy, not a live NAS mount. Large libraries can take a while — Destroy waits for the copy.'
                : ' Turn on to also copy installed games. Off by default — Steam CDN is often faster than uploading from this GPU to the NAS.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => mutation.mutate(!enabled)}
          disabled={mutation.isLoading}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
            enabled ? 'bg-sky-500' : 'bg-gray-600'
          } ${mutation.isLoading ? 'opacity-50' : ''}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default SteamPersistToggle;
